'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Mindmap, MindmapNode, MindmapNodeShape } from '@/features/mindmap/core/MindmapTypes';
import type { MindmapNodeStylePatch } from '@/features/mindmap/core/MindmapState';
import type { SetDocWithHistory } from './UseMindmapHistory';

type SubtreeClip = { rootId: string; nodes: MindmapNode[] } | null;

export interface UseMindmapClipboardOptions {
  doc: Mindmap;
  setDocWithHistory: SetDocWithHistory;
  genId: () => string;
  handleStyleCommit: (nodeId: string, patch: MindmapNodeStylePatch) => void;
  selectedRawIdsRef: React.MutableRefObject<string[]>;
  editingId: string | null;
}

export interface UseMindmapClipboardResult {
  copySubtree: (sourceId: string) => void;
  duplicateNodeOnly: (targetParentId: string) => void;
  duplicateSubtree: (targetParentId: string) => void;
  pasteStyleTo: (targetId: string) => void;
  getStyleClip: () => MindmapNodeStylePatch | null;
  canPaste: boolean;
  version: number;
}

export function useMindmapClipboard({
  doc,
  setDocWithHistory,
  genId,
  handleStyleCommit,
  selectedRawIdsRef,
  editingId,
}: UseMindmapClipboardOptions): UseMindmapClipboardResult {
  const clipboardRef = useRef<{ subtree: SubtreeClip; style: MindmapNodeStylePatch | null }>({ subtree: null, style: null });
  const [version, setVersion] = useState(0);

  const bump = () => setVersion((v) => v + 1);

  const copySubtree = useCallback(
    (sourceId: string) => {
      const idToChildren = new Map<string | null, MindmapNode[]>();
      for (const n of doc.nodes) {
        const k = (n.parentId ?? null) as string | null;
        const list = idToChildren.get(k) ?? [];
        list.push(n);
        idToChildren.set(k, list);
      }
      const queue: string[] = [sourceId];
      const seen = new Set<string>();
      const nodes: MindmapNode[] = [];
      while (queue.length) {
        const id = queue.shift()!;
        if (seen.has(id)) continue;
        seen.add(id);
        const n = doc.nodes.find((x) => x.id === id);
        if (n) {
          nodes.push({ ...n });
          const kids = idToChildren.get(id) ?? [];
          for (const k of kids) queue.push(k.id);
        }
      }
      const root = nodes.find((n) => n.id === sourceId);
      const stylePatch: MindmapNodeStylePatch | null = root
        ? {
            textColor: root.textColor,
            bgColor: root.bgColor,
            borderColor: root.borderColor,
            shape: (root.shape as MindmapNodeShape) ?? 'rounded',
          }
        : null;
      clipboardRef.current = { subtree: { rootId: sourceId, nodes }, style: stylePatch };
      bump();
    },
    [doc]
  );

  // No explicit copy-style action: copying a node captures its style.

  const duplicateNodeOnly = useCallback(
    (targetParentId: string) => {
      const clip = clipboardRef.current.subtree;
      if (!clip) return;
      const root = clip.nodes.find((n) => n.id === clip.rootId);
      if (!root) return;
      setDocWithHistory((prev) => {
        const next = { ...prev, nodes: [...prev.nodes] } as Mindmap;
        // Compute next order under parent
        let max = -1;
        for (const n of next.nodes) if (n.parentId === targetParentId) max = Math.max(max, n.order);
        const newNode: MindmapNode = {
          id: genId(),
          parentId: targetParentId,
          text: root.text,
          order: max + 1,
          collapsed: root.collapsed,
          textColor: root.textColor,
          bgColor: root.bgColor,
          borderColor: root.borderColor,
          shape: (root.shape as MindmapNodeShape) ?? undefined,
          link: root.link,
          fontSize: root.fontSize,
          lineHeight: root.lineHeight,
          boxShadow: root.boxShadow,
          borderStyle: root.borderStyle,
          borderWidth: root.borderWidth,
          borderRadius: root.borderRadius,
          styleTokens: root.styleTokens ? [...root.styleTokens] : undefined,
        };
        next.nodes.push(newNode);
        return next as Mindmap;
      });
    },
    [genId, setDocWithHistory]
  );

  const duplicateSubtree = useCallback(
    (targetParentId: string) => {
      const clip = clipboardRef.current.subtree;
      if (!clip) return;
      setDocWithHistory((prev) => {
        const next = { ...prev, nodes: [...prev.nodes] } as Mindmap;
        const idMap = new Map<string, string>();
        // Prepare order map for existing parents
        const orderMap = new Map<string | null, number>();
        for (const n of next.nodes) {
          const k = (n.parentId ?? null) as string | null;
          orderMap.set(k, Math.max(orderMap.get(k) ?? -1, n.order));
        }
        // First pass: create new ids
        for (const n of clip.nodes) idMap.set(n.id, genId());
        // Insert clones in original order
        for (const n of clip.nodes) {
          const newId = idMap.get(n.id)!;
          const newParent = n.id === clip.rootId ? targetParentId : idMap.get(n.parentId!)!;
          const k = (newParent ?? null) as string | null;
          const nextOrder = (orderMap.get(k) ?? -1) + 1;
          orderMap.set(k, nextOrder);
          const cloned: MindmapNode = {
            id: newId,
            parentId: newParent,
            text: n.text,
            order: nextOrder,
            collapsed: n.collapsed,
            textColor: n.textColor,
            bgColor: n.bgColor,
            borderColor: n.borderColor,
            shape: (n.shape as MindmapNodeShape) ?? undefined,
            link: n.link,
            fontSize: n.fontSize,
            lineHeight: n.lineHeight,
            boxShadow: n.boxShadow,
            borderStyle: n.borderStyle,
            borderWidth: n.borderWidth,
            borderRadius: n.borderRadius,
            styleTokens: n.styleTokens ? [...n.styleTokens] : undefined,
          };
          next.nodes.push(cloned);
        }
        return next;
      });
    },
    [genId, setDocWithHistory]
  );

  const getStyleClip = useCallback(() => clipboardRef.current.style ?? null, []);

  const pasteStyleTo = useCallback(
    (targetId: string) => {
      const clip = clipboardRef.current.style;
      if (!clip) return;
      handleStyleCommit(targetId, { ...clip });
    },
    [handleStyleCommit]
  );

  // Global keyboard: copy/paste hooks (Meta/Control)
  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (editingId) return; // allow native paste in inputs
      const isMeta = event.metaKey || event.ctrlKey;
      if (!isMeta) return;
      const activeEl = document.activeElement as HTMLElement | null;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA' || (activeEl as any).isContentEditable)) {
        return;
      }
      const selectedIds = selectedRawIdsRef.current;
      const primary = selectedIds[selectedIds.length - 1];
      if (!primary) return;

      const isKey = (k: string) => (event.key || '').toLowerCase() === k.toLowerCase();
      const isCode = (c: string) => (event.code || '').toLowerCase() === c.toLowerCase();

      if ((isKey('c') || isCode('keyc')) && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        copySubtree(primary);
        return;
      }
      // No Meta+Alt+C binding – style clip comes from normal copy
      if (isKey('v') || isCode('keyv')) {
        event.preventDefault();
        if (event.altKey) {
          pasteStyleTo(primary);
          return;
        }
        if (event.shiftKey) {
          duplicateSubtree(primary);
          return;
        }
        duplicateNodeOnly(primary);
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [copySubtree, duplicateNodeOnly, duplicateSubtree, editingId, pasteStyleTo, selectedRawIdsRef]);

  const canPaste = !!clipboardRef.current.subtree || !!clipboardRef.current.style;

  return { copySubtree, duplicateNodeOnly, duplicateSubtree, pasteStyleTo, getStyleClip, canPaste, version };
}

export default useMindmapClipboard;
